import { ApiClient } from '@/lib/axios/client';
import type { BotMessageCount, DeliveryLogListing, DeliveryLogParams, SendTestSummaryRequest, SendTestSummaryResponse, SendTestReminderRequest, SendTestReminderResponse, SimulateCommandRequest, SimulateCommandResponse } from './types';

export class DeliveryLogApi {
  // Downloads every log matching the filters (pagination ignored) as an .xlsx.
  static async exportExcel(orgId: string, params: Omit<DeliveryLogParams, 'page' | 'page_size'>): Promise<void> {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.set('category', params.category);
    if (params.status) queryParams.set('status', params.status);
    if (params.from) queryParams.set('from', params.from);
    if (params.to) queryParams.set('to', params.to);
    if (params.chatbot_code) queryParams.set('chatbot_code', params.chatbot_code);
    const res = await fetch(`/api/v1/orgs/${orgId}/delivery-logs/export?${queryParams}`, { credentials: 'include' });
    if (!res.ok) throw new Error('Export failed');
    const url = URL.createObjectURL(await res.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = 'delivery-logs.xlsx';
    link.click();
    URL.revokeObjectURL(url);
  }

  static async getLogs(orgId: string, params?: DeliveryLogParams): Promise<DeliveryLogListing> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.set('category', params.category);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.from) queryParams.set('from', params.from);
    if (params?.to) queryParams.set('to', params.to);
    if (params?.chatbot_code) queryParams.set('chatbot_code', params.chatbot_code);
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.page_size) queryParams.set('page_size', params.page_size.toString());

    const query = queryParams.toString();
    return await ApiClient.get<DeliveryLogListing>(
      `/api/v1/orgs/${orgId}/delivery-logs${query ? `?${query}` : ''}`
    );
  }

  static async getWeeklyMessageCounts(orgId: string): Promise<BotMessageCount[]> {
    return await ApiClient.get<BotMessageCount[]>(`/api/v1/orgs/${orgId}/delivery-logs/weekly-counts`);
  }

  static async sendTestSummary(orgId: string, data: SendTestSummaryRequest): Promise<SendTestSummaryResponse> {
    return await ApiClient.post<SendTestSummaryResponse>(
      `/api/v1/orgs/${orgId}/test-summary`,
      data
    );
  }

  static async sendTestReminder(orgId: string, data: SendTestReminderRequest): Promise<SendTestReminderResponse> {
    return await ApiClient.post<SendTestReminderResponse>(
      `/api/v1/orgs/${orgId}/test-reminder`,
      data
    );
  }

  static async simulateCommand(orgId: string, data: SimulateCommandRequest): Promise<SimulateCommandResponse> {
    return await ApiClient.post<SimulateCommandResponse>(
      `/api/v1/orgs/${orgId}/simulate-command`,
      data
    );
  }
}
